import React from 'react';
import { Link } from 'react-router-dom';

export interface ApplyModalProps {
  show: boolean;
  onClose: () => void;
  submissionMode: 'PROVIDER_PROFILE' | 'DIRECT_RESUME';
  setSubmissionMode: (mode: 'PROVIDER_PROFILE' | 'DIRECT_RESUME') => void;
  directSalary: string;
  setDirectSalary: (salary: string) => void;
  directResumeFileName: string;
  isUploadingResume: boolean;
  handleResumeFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  jobResumeDraft: Api.Resume.ProviderJobResumeDraftResponse | null;
  applySuccess: boolean;
  applyError: string;
  isSubmitting: boolean;
  onConfirmSubmit: () => Promise<void>;
}

export default function ApplyModal({
  show,
  onClose,
  submissionMode,
  setSubmissionMode,
  directSalary,
  setDirectSalary,
  directResumeFileName,
  isUploadingResume,
  handleResumeFileUpload,
  jobResumeDraft,
  applySuccess,
  applyError,
  isSubmitting,
  onConfirmSubmit,
}: ApplyModalProps) {
  if (!show) return null;

  const isDirectIncomplete = submissionMode === 'DIRECT_RESUME' && (!directSalary.trim() || !directResumeFileName);
  const isProfileIncomplete =
    submissionMode === 'PROVIDER_PROFILE' &&
    (!jobResumeDraft?.expected_monthly_salary?.trim() ||
      !(jobResumeDraft?.resume_file?.id || jobResumeDraft?.resume_file?.access_url || jobResumeDraft?.resume_file?.file_name));

  const isSubmitDisabled = isSubmitting || isUploadingResume || isDirectIncomplete || isProfileIncomplete;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/40 backdrop-blur-xs">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-foreground-400 hover:text-foreground-700 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="mb-4">
          <h3 className="text-xl font-bold text-foreground-950 mb-1">确认投递求职申请</h3>
          <p className="text-xs text-foreground-500">向招聘方提交您的求职资料或简历</p>
        </div>

        {applySuccess ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 text-green-600 text-2xl">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            <h4 className="font-semibold text-base text-foreground-900 mb-1">投递成功！</h4>
            <p className="text-xs text-foreground-500 mb-6">招聘方将会审核您的求职申请</p>
            <button
              onClick={onClose}
              className="w-full py-3 bg-primary-500 text-white rounded-xl font-medium text-sm hover:brightness-95 cursor-pointer"
            >
              好的，知道了
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {applyError && (
              <div className="p-3 bg-red-50 text-red-600 rounded-xl text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {applyError}
              </div>
            )}

            {/* 提交模式切换 Tab */}
            <div className="flex bg-gray-100/90 p-1 rounded-xl text-xs font-medium">
              <button
                type="button"
                onClick={() => setSubmissionMode('PROVIDER_PROFILE')}
                className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${submissionMode === 'PROVIDER_PROFILE'
                  ? 'bg-white text-primary-600 font-semibold shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                带入驻档案
              </button>
              <button
                type="button"
                onClick={() => setSubmissionMode('DIRECT_RESUME')}
                className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer ${submissionMode === 'DIRECT_RESUME'
                  ? 'bg-white text-primary-600 font-semibold shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                简历直投
              </button>
            </div>

            {submissionMode === 'DIRECT_RESUME' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">
                    期望月薪 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={directSalary}
                    onChange={(e) => setDirectSalary(e.target.value)}
                    placeholder="例如：10000-15000元 或 面议"
                    className="w-full px-3.5 py-2 text-xs bg-background-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">
                    简历文件 <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(支持 PDF、Word、ZIP 等)</span>
                  </label>
                  {directResumeFileName ? (
                    <div className="flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs">
                      <div className="flex items-center gap-2 truncate pr-2">
                        <i className="ri-file-text-line text-primary-500 text-base shrink-0"></i>
                        <span className="font-medium text-foreground-900 truncate">{directResumeFileName}</span>
                      </div>
                      <label className="text-xs text-primary-600 hover:underline cursor-pointer shrink-0">
                        更换文件
                        <input
                          type="file"
                          onChange={handleResumeFileUpload}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.zip,.rar,.png,.jpg,.jpeg"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-gray-300 hover:border-primary-400 rounded-xl bg-gray-50/50 hover:bg-primary-50/20 text-xs text-foreground-600 cursor-pointer transition-all">
                      {isUploadingResume ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                          <span>正在上传简历文件...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-2-line text-base text-primary-500"></i>
                          <span>点击上传简历附件</span>
                        </>
                      )}
                      <input
                        type="file"
                        onChange={handleResumeFileUpload}
                        disabled={isUploadingResume}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.zip,.rar,.png,.jpg,.jpeg"
                      />
                    </label>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-background-50 rounded-2xl border border-gray-200/80 space-y-2.5">
                <div className="text-xs font-semibold text-foreground-700 border-b border-gray-200/60 pb-2 flex justify-between items-center">
                  <span>我的简历</span>
                  <Link to="/my-job-resume" className="text-primary-600 hover:underline text-xs font-normal">
                    修改资料
                  </Link>
                </div>

                <div className="text-xs space-y-1.5 text-foreground-600">
                  <div className="flex justify-between">
                    <span className="text-foreground-400">期望月薪：</span>
                    <span className="font-medium text-foreground-900">
                      {jobResumeDraft?.expected_monthly_salary || '未填写'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-400">简历附件：</span>
                    <span className="font-medium text-foreground-900 truncate max-w-[180px]">
                      {jobResumeDraft?.resume_file?.file_name || '未上传附件'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={onClose}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-medium text-foreground-600 hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={onConfirmSubmit}
                disabled={isSubmitDisabled}
                className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${isSubmitDisabled
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:brightness-95 cursor-pointer'
                  }`}
              >
                {isSubmitting && <i className="ri-loader-4-line animate-spin"></i>}
                确认投递
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
